/**
 * JSU Global — front-page interactivity
 *
 * Mirrors the behaviors of the legacy Vue components in
 * frontend/components/shared/JSU/home/* using their original libraries:
 *   - SwiperJS (effect: fade) for the hero rotator (hero.vue)
 *   - CountUp.js with scrollSpy for the stats counters (stats.vue)
 */

import "../css/main.scss";
import "./main.js";

// SwiperJS — bundle includes core + all module CSS so the fade effect works.
import Swiper from "swiper/bundle";
import "swiper/css/bundle";

// CountUp.js — same engine that vue-countup-v3 wraps.
import { CountUp } from "countup.js";

document.addEventListener("DOMContentLoaded", () => {
    initFrontpageModal();
    initHeroSwiper();
    initTestimonialsSwiper();
    initStatsCountUp();
    loadEmbedSocialScripts();
    initClubSearch();
    initClubsListSearch();
});

/**
 * Front-page welcome modal. Mirrors SharedModal in
 * frontend/components/shared/JSU/home/index.vue: shows on every page load.
 */
function initFrontpageModal() {
    const $ = window.jQuery;
    if (!$ || typeof $.fn.lightbox_me !== "function") return;

    const $modal = $("[data-jsu-fp-modal]");
    if (!$modal.length) return;

    // "Enter the Website" / explicit close affordances trigger lightbox_me's close.
    $modal.on("click", "[data-jsu-fp-modal-close]", function (ev) {
        if (this.tagName !== "A") ev.preventDefault();
        $modal.trigger("close");
    });

    $modal.removeClass("hide").lightbox_me({
        zIndex: 10000,
        closeClick: false,
        centered: true,
        closeSelector: ".lightbox-close",
        overlayCSS: { background: "black", opacity: 0.4 }
    });
}

/**
 * Hero rotator. Matches hero.vue's <Swiper> options exactly:
 *   modules: Autoplay + EffectFade
 *   slidesPerView: 1, loop: true, autoHeight: true
 *   effect: 'fade', fadeEffect.crossFade: true
 *   autoplay: { delay: 4000 }
 */
function initHeroSwiper() {
    const el = document.querySelector("[data-fp-hero-swiper]");
    if (!el) return;

    new Swiper(el, {
        slidesPerView: 1,
        loop: true,
        autoHeight: true,
        effect: "fade",
        fadeEffect: { crossFade: true },
        autoplay: {
            delay: 4000,
            disableOnInteraction: false,
        },
    });
}

/**
 * Testimonials slider. Matches slider.vue's <Swiper> options:
 *   modules: Autoplay + EffectCreative + Pagination
 *   loop: true, spaceBetween: 30
 *   autoplay: { delay: 4000, disableOnInteraction: true }
 *   breakpoints: 320 -> 1.0 spv, 1200 -> 1.2 spv
 */
function initTestimonialsSwiper() {
    const el = document.querySelector("[data-fp-testimonials-swiper]");
    if (!el) return;

    new Swiper(el, {
        slidesPerView: 1.2,
        spaceBetween: 30,
        loop: true,
        autoplay: {
            delay: 4000,
            disableOnInteraction: true,
        },
        navigation: {
            prevEl: "[data-fp-testimonials-prev]",
            nextEl: "[data-fp-testimonials-next]",
        },
        breakpoints: {
            320: {
                slidesPerView: 1,
                spaceBetween: 30,
            },
            1200: {
                slidesPerView: 1.2,
                spaceBetween: 30,
            },
        },
    });
}

/**
 * Stats counters. Matches stats.vue's CountUp options:
 *   useEasing: true, useGrouping: true, duration: 10
 *   separator: ' ', decimal: ',', enableScrollSpy: true
 *
 * Per-element `data-count-target` and `data-count-suffix` come from
 * templates/modules/frontpage/stats.php.
 */
function initStatsCountUp() {
    const counters = document.querySelectorAll(".stats__count");
    if (!counters.length) return;

    const observer = "IntersectionObserver" in window
        ? new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.target._countUp) {
                    entry.target._countUp.start();
                    obs.unobserve(entry.target);
                }
            });
        }, { threshold: 0.2 })
        : null;

    counters.forEach((el) => {
        const endVal = parseInt(el.dataset.countTarget || "0", 10);
        const suffix = el.dataset.countSuffix || "";

        const instance = new CountUp(el, endVal, {
            useEasing: true,
            useGrouping: true,
            duration: 2.5,
            separator: ",",
            decimal: ".",
            prefix: "",
            suffix: suffix,
        });

        if (instance.error) {
            // Fallback: write the final value directly.
            el.textContent = endVal.toLocaleString() + suffix;
            return;
        }

        if (observer) {
            el._countUp = instance;
            observer.observe(el);
        } else {
            instance.start();
        }
    });
}

/**
 * Inject EmbedSocial scripts once if a hashtag widget is on the page.
 * Replaces the inline `useHead({ script: [...] })` from embed.vue.
 */
function loadEmbedSocialScripts() {
    if (!document.querySelector(".embedsocial-hashtag")) return;

    const scripts = [
        ["EmbedSocialGalleryScript",   "https://embedsocial.com/embedscript/bi.js"],
        ["EmbedSocialInstagramScript", "https://embedsocial.com/embedscript/in.js"],
        ["EmbedSocialHashtagScript",   "https://embedsocial.com/cdn/ht.js"],
    ];

    scripts.forEach(([id, src]) => {
        if (document.getElementById(id)) return;
        const s = document.createElement("script");
        s.id = id;
        s.src = src;
        s.async = true;
        document.head.appendChild(s);
    });
}

/**
 * Front-page club autocomplete search.
 *
 * Mirrors frontend/components/shared/JSU/home/club.vue:
 *   - debounced live search as the user types
 *   - dropdown of matching clubs (links to /Club/{slug}/)
 *   - "didn't find your club?" CTA when zero results
 *
 * Hits the WordPress REST API for the `Club` CPT.
 */
function initClubSearch() {
    const form = document.getElementById("searchform");
    if (!form || !form.classList.contains("fp__club__search")) return;

    const input      = form.querySelector("#club-search-input");
    const resultsEl  = form.querySelector("#club-search-results");
    const loadingEl  = form.querySelector("#club-search-loading");
    const noResults  = form.querySelector("#club-search-noresults");
    if (!input || !resultsEl) return;

    const siteUrl = (window.jsuGlobal && window.jsuGlobal.siteUrl) || "";
    const restUrl = siteUrl.replace(/\/$/, "") + "/wp-json/wp/v2/Club";

    let activeController = null;

    const hideAll = () => {
        resultsEl.hidden = true;
        resultsEl.innerHTML = "";
        if (loadingEl)  loadingEl.hidden = true;
        if (noResults)  noResults.hidden = true;
    };

    const showLoading = () => {
        if (loadingEl) loadingEl.hidden = false;
        resultsEl.hidden = true;
        if (noResults) noResults.hidden = true;
    };

    const renderResults = (items) => {
        if (loadingEl) loadingEl.hidden = true;
        if (!items.length) {
            resultsEl.hidden = true;
            resultsEl.innerHTML = "";
            if (noResults) noResults.hidden = false;
            return;
        }
        if (noResults) noResults.hidden = true;
        resultsEl.innerHTML = items
            .map((item) => {
                const title = (item.title && item.title.rendered) || item.slug || "";
                const href  = item.link || (siteUrl.replace(/\/$/, "") + "/Club/" + item.slug + "/");
                return (
                    '<a href="' + href + '">' +
                    '<span class="title">' + title + "</span>" +
                    "</a>"
                );
            })
            .join("");
        resultsEl.hidden = false;
    };

    const doSearch = async (term) => {
        if (activeController) activeController.abort();
        activeController = new AbortController();

        showLoading();

        try {
            const url = restUrl + "?search=" + encodeURIComponent(term) + "&per_page=10&_fields=id,slug,link,title";
            const res = await fetch(url, { signal: activeController.signal });
            if (!res.ok) throw new Error("HTTP " + res.status);
            const data = await res.json();
            renderResults(Array.isArray(data) ? data : []);
        } catch (err) {
            if (err.name === "AbortError") return;
            if (loadingEl) loadingEl.hidden = true;
            // On error, just hide everything silently.
            resultsEl.hidden = true;
            if (noResults) noResults.hidden = true;
        }
    };

    let debounceTimer = null;
    input.addEventListener("input", () => {
        const term = input.value.trim();
        clearTimeout(debounceTimer);
        if (!term) {
            hideAll();
            return;
        }
        // Fire quickly so the dropdown updates as the user types.
        debounceTimer = setTimeout(() => doSearch(term), 150);
    });

    // Hide dropdown when clicking outside the form.
    document.addEventListener("click", (e) => {
        if (!form.contains(e.target)) {
            resultsEl.hidden = true;
        }
    });

    // Re-show dropdown if user re-focuses the input and it still has content.
    input.addEventListener("focus", () => {
        if (resultsEl.innerHTML.trim() !== "") {
            resultsEl.hidden = false;
        }
    });
}

/**
 * /clubs index — live filter of the grouped clubs list as the user types
 * in the header search field. Mirrors master:frontend/pages/clubs/index.vue's
 * SharedJSUComponentsSearch behavior, but works against the already-rendered
 * DOM (server pre-renders all clubs grouped by region) so it stays fast and
 * works without GraphQL.
 */
function initClubsListSearch() {
    const input = document.getElementById("s");
    const list  = document.getElementById("jsu-clubs-list");
    const empty = document.getElementById("jsu-clubs-empty");
    if (!input || !list) return;

    const groups = Array.from(list.querySelectorAll(".clubs__group"));
    if (!groups.length) return;

    const cards = groups.map((group) => ({
        group,
        items: Array.from(group.querySelectorAll(".clubs__col")).map((el) => ({
            el,
            text: (
                (el.querySelector(".clubs__col__title")?.textContent || "") +
                " " +
                (el.querySelector(".clubs__col__loc")?.textContent || "")
            ).toLowerCase(),
        })),
    }));

    const apply = () => {
        const q = input.value.trim().toLowerCase();
        let anyVisible = false;

        cards.forEach(({ group, items }) => {
            let groupVisible = 0;
            items.forEach(({ el, text }) => {
                const match = q === "" || text.indexOf(q) !== -1;
                el.style.display = match ? "" : "none";
                if (match) groupVisible++;
            });
            group.style.display = groupVisible ? "" : "none";
            if (groupVisible) anyVisible = true;
        });

        if (empty) empty.hidden = anyVisible;
    };

    input.addEventListener("input", apply);
    apply();
}
